// Admin console: the burn approval queue.
//
// A burn consumes write-once medium and bills the customer, so a customer's
// click no longer seals anything — it files a request that lands here. The DMD
// is built only after an operator approves, which makes this page the gate
// rather than a report.
//
// Pending requests sort first, oldest first: the one that has waited longest is
// the one someone is waiting on.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { adminApi } from '../api/adminClient';
import type { BurnRequest } from '../api/types';
import { usePaginated } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatDateTime, formatEUR, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { Field } from '../components/Field';
import { useToast } from '../components/Toast';
import { Badge, Button, Card } from '../components/ui';
import { IconArchive, IconClose } from '../components/Icons';

const PAGE = 25;

function statusTone(status: string): 'accent' | 'info' | 'warning' | 'danger' | 'success' {
  switch (status) {
    case 'requested':
      return 'warning';
    case 'building':
      return 'info';
    case 'completed':
      return 'success';
    case 'rejected':
    case 'failed':
      return 'danger';
    default:
      return 'accent';
  }
}

export function AdminBurnRequests() {
  const { t } = useTranslation();
  const lang = useLang();
  const toast = useToast();
  const [pendingOnly, setPendingOnly] = useState(true);
  const [rejecting, setRejecting] = useState<BurnRequest | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const list = usePaginated((cursor) => adminApi.adminBurnRequests(PAGE, cursor, pendingOnly), [pendingOnly]);

  const approve = async (req: BurnRequest) => {
    setBusyId(req.id);
    try {
      await adminApi.adminApproveBurn(req.id);
      // The build runs in the background; the row moves to "building" and the
      // operator can carry on with the queue.
      toast.success(t('admin.burnQueue.approved', { archive: req.archiveName || req.archiveId }));
      list.reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader title={t('admin.burnQueue.title')} subtitle={t('admin.burnQueue.subtitle')} />

      <Card>
        <div className="card__body">
          <label className="row" style={{ gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
            {t('admin.burnQueue.pendingOnly')}
          </label>
        </div>

        <ApiState
          status={list.status}
          error={list.error}
          data={list.status === 'error' ? null : list.items}
          onRetry={list.reload}
        >
          {(items: BurnRequest[]) =>
            items.length === 0 ? (
              <EmptyState
                title={pendingOnly ? t('admin.burnQueue.emptyPending') : t('admin.burnQueue.empty')}
                icon={<IconArchive width={26} height={26} />}
              />
            ) : (
              <>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t('admin.burnQueue.requested')}</th>
                        <th>{t('admin.burns.customer')}</th>
                        <th>{t('admin.burns.bucket')}</th>
                        <th className="num">{t('admin.burns.objects')}</th>
                        <th className="num">{t('admin.burnQueue.volume')}</th>
                        <th className="num">{t('admin.burnQueue.cost')}</th>
                        <th>{t('admin.burnQueue.status')}</th>
                        <th>{t('admin.burnQueue.decision')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((b) => (
                        <tr key={b.id}>
                          <td>
                            {formatDateTime(b.requestedAt, lang)}
                            <div className="muted" style={{ fontSize: 12 }}>{b.requestedBy}</div>
                          </td>
                          <td className="cell-strong">
                            <Link to={`/customers/${encodeURIComponent(b.tenantId)}`}>
                              {b.tenantName || b.tenantId}
                            </Link>
                          </td>
                          <td>{b.archiveName || b.archiveId}</td>
                          <td className="num">{formatNumber(b.estObjects, lang)}</td>
                          <td className="num">{formatBytes(b.estBytes, lang)}</td>
                          {/* The quote frozen at request time — the figure the
                              customer agreed to, not one re-derived now. */}
                          <td className="num">{formatEUR(b.estCostEur, lang)}</td>
                          <td>
                            <Badge tone={statusTone(b.status)}>
                              {t(`admin.burnQueue.state.${b.status}`, b.status)}
                            </Badge>
                            {b.error ? (
                              <div style={{ fontSize: 12, color: 'var(--danger)' }}>{b.error}</div>
                            ) : null}
                            {b.note && b.status === 'rejected' ? (
                              <div className="muted" style={{ fontSize: 12 }}>{b.note}</div>
                            ) : null}
                          </td>
                          <td>
                            {b.pending ? (
                              <span className="row" style={{ gap: 6 }}>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  disabled={busyId === b.id}
                                  onClick={() => approve(b)}
                                >
                                  {t('admin.burnQueue.approve')}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={busyId === b.id}
                                  onClick={() => setRejecting(b)}
                                >
                                  {t('admin.burnQueue.reject')}
                                </Button>
                              </span>
                            ) : (
                              <span className="muted" style={{ fontSize: 13 }}>
                                {b.decidedBy ? t('admin.burnQueue.decidedBy', { who: b.decidedBy }) : '—'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onLoadMore={list.loadMore} />
              </>
            )
          }
        </ApiState>
      </Card>

      {rejecting ? (
        <RejectDialog
          req={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            list.reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Rejection needs a reason: the customer sees it, and "no" without a why
 *  simply moves the conversation to support. */
function RejectDialog({ req, onClose, onDone }: { req: BurnRequest; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) {
      setError(t('admin.burnQueue.reasonRequired'));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await adminApi.adminRejectBurn(req.id, note.trim());
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="rj-title">
        <div className="row between">
          <h2 className="dialog__title" id="rj-title">
            {t('admin.burnQueue.rejectTitle', { archive: req.archiveName || req.archiveId })}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>
        <div style={{ marginTop: 16 }}>
          <Field label={t('admin.burnQueue.reason')} required error={error} hint={t('admin.burnQueue.reasonHint')}>
            {(p) => (
              <input className="input" {...p} value={note} autoFocus onChange={(e) => setNote(e.target.value)} />
            )}
          </Field>
        </div>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="danger" disabled={busy}>
            {t('admin.burnQueue.reject')}
          </Button>
        </div>
      </form>
    </div>
  );
}
